/*郵便番号*/
import React, { useState } from 'react';
import { Form, Col, Row, Button } from 'react-bootstrap';
import axios from 'axios';
import RowTemplate from '../atoms/RowTemplate';
import LoadingModal from './LoadingModal';
import Messages from './Messages';
import Paragraph from './Paragraph';
import SearchAddress from './SearchAddress';
import SearchAddressError from './SearchAddressError';
import { http_status_code_list, network_error_message, postcode_splitter } from '../Const';
import { authErrorTimeout } from '../Common';
interface Props {
  id: string;
  name: string;
  label: string;
  need?: boolean;
  value: string[];
  disabled?: boolean;
  message?: string;
  help_msg?: string;
  placeholder: string;
  looks_inactive?: boolean;
  handleSelectAddress: (
    address_field_column_name: string,
    address: string,
    kana_address_field_column_name: string,
    kana_address: string
  ) => void;
  handleChangePostCode1?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangePostCode2?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlurPostcode1?: VoidFunction;
  handleBlurPostcode2?: VoidFunction;
  readonly?: boolean;
}

interface AddressInfo {
  address: string;
  kana_address: string;
}

const Postcode: React.FC<Props> = ({
  id,
  name,
  label,
  need,
  value,
  disabled,
  message,
  help_msg,
  placeholder,
  looks_inactive,
  handleSelectAddress,
  handleChangePostCode1,
  handleChangePostCode2,
  handleBlurPostcode1,
  handleBlurPostcode2,
  readonly,
}) => {
  const [modal_open, setModalOpen] = useState(false);
  const [address_list, setAddressList] = useState([]);
  const [target_column, setTargetColumn] = useState('');
  const [target_kana_column, setTargetKanaColumn] = useState('');
  const [error_msg, setErrorMsg] = useState('');
  const [link_label, setLinkLabel] = useState('');
  const [link_anchor, setLinkAnchor] = useState('');
  const [is_error, setIsError] = useState(false);
  const [is_loading, setIsLoading] = useState(false);
  const [is_view_link, setIsViewLink] = useState(false);

  // 検索ボタン押下時
  const handleClickSearch = () => searchAddress();

  // モーダルを閉じる
  const handleClickClose = () => setModalOpen(false);

  // 郵便番号住所検索機能バリデーションエラーポップアップのボタン
  const popupButton = {
    label: '確認',
    variant: 'success',
    handleClick: () => setIsError(false),
  };

  // 住所検索
  const searchAddress = async () => {
    setIsLoading(true);
    await axios
      .post(`${location.origin}${process.env.MIX_MOCK_URL}/search_address`, {
        field_column_name: name,
        postcode: value.join(''),
      })
      .then(res => {
        const addressList = res.data.detail;
        const targetFieldColumnName: string = res.data.target_address_field;
        const targetKanaFieldColumnName: string = res.data.target_kana_address_field;
        setAddressList(addressList);
        setTargetColumn(targetFieldColumnName);
        setTargetKanaColumn(targetKanaFieldColumnName);

        // 検索件数による条件分け
        if (addressList.length === 0) {
          setIsError(true);
          setIsViewLink(true);
          setErrorMsg('郵便局のホームページで住所から郵便番号を検索の上ご入力ください');
          setLinkLabel('郵便番号検索ページへ');
          setLinkAnchor('https://www.post.japanpost.jp/zipcode/');
        } else if (addressList.length === 1) {
          handleSelectAddress!(
            targetFieldColumnName,
            addressList[0].address,
            targetKanaFieldColumnName,
            addressList[0].kana_address
          );
        } else if (addressList.length >= 2) setModalOpen(true);
      })
      .catch(e => {
        // ネットワークエラーなので認証エラーとしてタイムアウト画面に飛ばす
        if (e.message === network_error_message) {
          return authErrorTimeout(http_status_code_list.unauthorized);
        }
        // バリデーションエラー
        setIsError(true);
        setIsViewLink(false);
        setErrorMsg(e.response.data.message);
      });
    setIsLoading(false);
  };

  // ポップアップ内の住所一覧を押下時
  const popupClick = (value: AddressInfo) => {
    handleSelectAddress!(target_column, value.address, target_kana_column, value.kana_address);
    setModalOpen(false);
  };

  /**
   * プレースホルダを配列に変換
   * @return string[] プレースホルダの配列
   */
  const setPlaceholder = (): string[] => {
    if (placeholder === null || typeof placeholder === 'undefined' || placeholder.length === 0) {
      return ['', ''];
    }
    return placeholder.split(postcode_splitter.placeholder);
  };

  /**
   * ラベルを描画する
   * @return JSX.Element ラベルのElement
   */
  const returnLabelJsx = () => {
    return (
      <Row>
        <Col>
          <Paragraph label={value && value[0] && value[1] ? `${value[0]}-${value[1]}` : ''} />
        </Col>
      </Row>
    );
  };

  /**
   * 通常のPostcodeを描画する
   * @return JSX.Element ラベルのElement
   */
  const returnPostCodeJsx = () => {
    return (
      <>
        <Row>
          <Col className="pr-1">
            <Form.Control
              type="text"
              id={`${id}_last`}
              onChange={handleChangePostCode1}
              value={value[0] ? value[0] : ''}
              placeholder={setPlaceholder()[0]}
              disabled={disabled || readonly}
              maxLength={3}
              onBlur={handleBlurPostcode1}
              className={message && 'textarea-error'}
            />
          </Col>
          <Col className="px-1">
            <Form.Control
              type="text"
              id={`${id}_fast`}
              onChange={handleChangePostCode2}
              value={value[1] ? value[1] : ''}
              placeholder={setPlaceholder()[1]}
              disabled={disabled || readonly}
              maxLength={4}
              onBlur={handleBlurPostcode2}
              className={message && 'textarea-error'}
            />
          </Col>
          <Col xs={5} className="text-btn_area px-1">
            <Button size="sm" variant="success" className="btn-form" onClick={handleClickSearch}>
              検索
            </Button>
          </Col>
        </Row>
        <Messages help_msg={help_msg} valid_msg={message} />
        <SearchAddress
          toggleShow={modal_open}
          toggleClose={handleClickClose}
          popupClick={popupClick}
          address_list={address_list}
        />
        <SearchAddressError
          isShow={is_error}
          isViewLink={is_view_link}
          toggleClose={() => setIsError(false)}
          text={error_msg}
          button={popupButton}
          link_label={link_label}
          link_anchor={link_anchor}
        />
        <LoadingModal show={is_loading} />
      </>
    );
  };

  return (
    <RowTemplate
      label={label}
      need={need}
      disabled={disabled}
      looks_inactive={looks_inactive}
      content={<>{disabled || looks_inactive ? returnLabelJsx() : returnPostCodeJsx()}</>}
    />
  );
};

export default Postcode;
